import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/auth/me');
      const userData = response.data.user;
      if (response.data.organization) {
        userData.organization = response.data.organization;
      }
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only clear token if we get a definitive 401 or 403
      if (error.response?.status === 401 || error.response?.status === 403) {
        setToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password, organizationSlug) => {
    try {
      // Pass the device token if available
      const deviceToken = localStorage.getItem('device_token');
      const headers = deviceToken ? { 'x-device-token': deviceToken } : {};

      const response = await api.post('/auth/login', {
        email,
        password,
        organization_slug: organizationSlug
      }, { headers });

      // Handle 2FA Requirement
      if (response.data.require_2fa) {
        return {
          success: false,
          require_2fa: true,
          temp_user_id: response.data.temp_user_id,
          message: response.data.message
        };
      }

      const { token: newToken, refresh_token: newRefreshToken, user: userData, organization } = response.data;

      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refresh_token', newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      if (organization) {
        userData.organization = organization;
      }

      setUser(userData);
      setLoading(false);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const verify2FA = async (userId, code, trustDevice) => {
    try {
      const response = await api.post('/auth/verify-2fa', {
        user_id: userId,
        code,
        trust_device: trustDevice
      });

      const { token: newToken, refresh_token: newRefreshToken, user: userData, device_token: deviceToken, organization } = response.data;

      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refresh_token', newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      if (deviceToken) {
        localStorage.setItem('device_token', deviceToken);
      }

      if (organization) {
        userData.organization = organization;
      }

      setUser(userData);
      setLoading(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Verification failed'
      };
    }
  };

  const resend2FA = async (userId) => {
    try {
      await api.post('/auth/resend-2fa', { user_id: userId });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend code'
      };
    }
  };

  const register = async (organizationData, userDataInput) => {
    try {
      const response = await api.post('/auth/register', {
        organization: organizationData,
        user: userDataInput
      });

      const { token: newToken, refresh_token: newRefreshToken, user: newUser, organization } = response.data;

      setToken(newToken);
      localStorage.setItem('token', newToken);
      localStorage.setItem('refresh_token', newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      if (organization) {
        newUser.organization = organization;
      }

      setUser(newUser);
      setLoading(false);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setLoading(false);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    delete api.defaults.headers.common['Authorization'];
  };

  const updateUser = async () => {
    await fetchUser();
  };

  const permissionSet = useMemo(() => {
    if (!user?.permissions) return new Set();
    // Support both string array and object array for backward compatibility
    const codes = user.permissions.map((perm) => (typeof perm === 'string' ? perm : perm.code)).filter(Boolean);
    const set = new Set(codes);
    // console.log('[AuthContext] Calculated Permission Set:', Array.from(set));
    return set;
  }, [user]);

  const hasPermission = useCallback(
    (permissionCode) => {
      if (!permissionCode) return false;
      if (!user) return false;

      // Admin wildcard
      if (permissionSet.has('*') || user.role === 'org_admin') return true;

      // Exact match
      if (permissionSet.has(permissionCode)) return true;

      // Category wildcard (e.g., 'documents.*')
      const [category] = permissionCode.split('.');
      if (permissionSet.has(`${category}.*`)) return true;

      return false;
    },
    [permissionSet, user]
  );

  const hasAnyPermission = useCallback(
    (permissionCodes = []) => {
      if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return false;
      return permissionCodes.some((code) => hasPermission(code));
    },
    [hasPermission]
  );

  const hasAllPermissions = useCallback(
    (permissionCodes = []) => {
      if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) return false;
      return permissionCodes.every((code) => hasPermission(code));
    },
    [hasPermission]
  );

  const value = {
    user,
    token,
    loading,
    login,
    verify2FA,
    resend2FA,
    register,
    logout,
    updateUser,
    permissions: user?.permissions || [],
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAuthenticated: !!user && !loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
